import React, { useState, useEffect, useRef } from "react";
import Spline from "@splinetool/react-spline";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link as RouterLink,
  useLocation,
} from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";

// import loadingGif1 from "./loading.gif";
// import loadingGif2 from "./loading2.gif";
// import loadingGif3 from "./loading3.gif";
// import loadingGif4 from "./loading4.gif";
// import loadingGif5 from "./loading5.gif";
// import loadingDoneGif from "./loadingDone.gif";

const GlobalStyle = createGlobalStyle`
  * { margin: 0; padding: 0; box-sizing: border-box; }

  html, body { width: 100%; height: 100%; overflow: hidden; }

  #root { width: 100%; height: 100%; }

  body {
    font-family: 'Spline Sans Mono', monospace;
    background: #000; /* fallback background */
  }
`;

const Logo = styled.div`
  position: absolute;
  bottom: 23px;
  right: 18px;
  z-index: 1000;
  background-color: #000000;
  color: white;
  border-radius: 30%;
  width: 150px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  font-weight: bold;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.9);
  cursor: pointer;
  text-transform: uppercase;
`;

const Navbar = styled.nav`
  position: absolute;
  top: 5%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 30px;
  width: 90%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.6);
  z-index: 10;
  background: rgba(0, 0, 0, 0.4);

  @media (min-width: 1200px) {
    width: 60%;
  }
`;

const NavItems = styled.ul`
  list-style: none;
  display: flex;
  gap: 20px;
  align-items: center;
  margin: 0;
  padding: 0;

  @media (max-width: 900px) {
    gap: 1px;
  }
`;

const NavItem = styled.li`
  position: relative;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  padding: 8px 8px;
  color: white;

  &:hover::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.2);
    z-index: -1;
  }

  @media (max-width: 900px) {
    font-size: 0.8rem;
  }
  @media (max-width: 600px) {
    font-size: 0.4rem;
  }
`;

const DropdownMenu = styled.ul`
  list-style: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  padding: 6px 0;
  background: rgba(255, 255, 255, 0.09);
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.7);
  display: none;
  flex-direction: column;
  width: max-content;
  min-width: 120px;
  max-width: calc(100vw - 40px);
  overflow-wrap: break-word;
  z-index: 999;
  right: auto;

  @media (max-width: 900px) {
    left: 0;
    transform: none;
    max-width: calc(100vw - 60px);
    min-width: 100px;
  }
  @media (max-width: 600px) {
    left: 0;
    transform: none;
    max-width: calc(100vw - 40px);
    min-width: 80px;
    font-size: 0.8rem;
  }
`;

const DropdownItem = styled.li`
  padding: 8px 12px;
  white-space: nowrap;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  a {
    color: white;
    text-decoration: none;
    font-size: 0.9rem;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 900px) {
    padding: 6px 10px;
    a {
      font-size: 0.75rem;
      white-space: normal;
      overflow: visible;
      text-overflow: initial;
      line-height: 1.3;
    }
  }
  @media (max-width: 600px) {
    padding: 6px 8px;
    a {
      font-size: 0.65rem;
      white-space: normal;
      overflow: visible;
      text-overflow: initial;
      line-height: 1.4;
    }
  }
`;

const DropdownWrapper = styled(NavItem)`
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;

  &:hover ${DropdownMenu} {
    display: flex;
  }

  &::after {
    content: "▼";
    font-size: 0.7rem;
    color: white;
    transition: transform 0.2s ease;
  }
  &:hover::after {
    transform: rotate(180deg);
  }

  @media (max-width: 900px) {
    &:last-child ${DropdownMenu} {
      right: 0;
      left: auto;
      transform: none;
    }
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background: #000;
`;

const StyledLink = styled(RouterLink)`
  color: white;
  text-decoration: none;
`;
const Link = styled.a`
  color: white;
  text-decoration: none;
`;

const Title = styled.h1`
  color: white;
  font-size: 1.5rem;
  margin: 0;
  font-family: "Spline Sans Mono", monospace;
  @media (max-width: 900px) {
    font-size: 0.9rem;
  }
  @media (max-width: 600px) {
    font-size: 0.6rem;
  }
`;

const TitleLink = styled(RouterLink)`
  color: white;
  font-size: 1.5rem;
  margin: 0;
  text-decoration: none;
  font-family: "Spline Sans Mono", monospace;
  &:hover {
    color: #9400a6;
    cursor: pointer;
  }
  @media (max-width: 1350px) {
    font-size: 1.2rem;
  }
  @media (max-width: 900px) {
    font-size: 1rem;
  }
  @media (max-width: 600px) {
    font-size: 0.7rem;
  }
`;

const SceneWrapper = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
  visibility: hidden;
  transition: opacity 1s ease, visibility 0s linear 1s;

  &.visible {
    opacity: 1;
    visibility: visible;
    transition: opacity 1s ease, visibility 0s linear 0s;
  }
`;

const fadeIn = keyframes` from { opacity: 0 } to { opacity: 1 } `;
const fadeOut = keyframes` from { opacity: 1 } to { opacity: 0 } `;
const progress = keyframes`
  0% { width: 0% } 85% { width: 95% } 100% { width: 100% }
`;

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  display: ${(p) => (p.$visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.85);
  z-index: 2000;
  animation: ${(p) => (p.$visible ? fadeIn : fadeOut)} 200ms ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const OverlayCard = styled.div`
  width: min(520px, 92vw);
  padding: 28px 24px;
  border-radius: 16px;
  background: rgba(20, 20, 20, 0.7);
  backdrop-filter: blur(6px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.6);
  color: white;
  text-align: center;
`;

const OverlayTitle = styled.div`
  font-weight: 700;
  font-size: 1.1rem;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  background: #ffffff;
  opacity: 0.9;
  border-radius: 999px;
  animation: ${progress} 1500ms ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    width: 100%;
  }
`;

const OverlayNote = styled.div`
  margin-top: 10px;
  opacity: 0.7;
  font-size: 0.9rem;
`;

function RouteTransitionOverlay({
  showOnFirstMount = true,
}: {
  showOnFirstMount?: boolean;
}) {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const isFirstTick = useRef(true);
  const timerRef = useRef<number | null>(null);

  // On mount (first page load), show the overlay if requested
  useEffect(() => {
    if (!showOnFirstMount) return;
    setShow(true);
    timerRef.current = window.setTimeout(() => {
      setShow(false);
      timerRef.current = null;
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [showOnFirstMount]);

  // On location change, show the overlay for 4s (skip the very first tick if we already showed it above)
  useEffect(() => {
    // If we already handled initial mount with overlay, the first location change is still the same path.
    if (isFirstTick.current) {
      isFirstTick.current = false;
      // If we DID NOT show at mount, and we want to show only on route changes, do nothing here.
      return;
    }

    // Restart timer cleanly for rapid nav
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setShow(true);
    timerRef.current = window.setTimeout(() => {
      setShow(false);
      timerRef.current = null;
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location]);

  return (
    <Overlay
      aria-hidden={!show}
      role="status"
      aria-live="polite"
      $visible={show}
    >
      <OverlayCard>
        <OverlayTitle>Loading</OverlayTitle>
        <BarTrack>
          <BarFill />
        </BarTrack>
        <OverlayNote>Preparing the next view…</OverlayNote>
      </OverlayCard>
    </Overlay>
  );
}

type SplineWithLoaderProps = { scene: string };

function SplineWithLoader({ scene }: SplineWithLoaderProps) {
  const [sceneVisible, setSceneVisible] = useState(false);

  // Mount immediately; fade in the scene when Spline fires onLoad
  useEffect(() => {
    setSceneVisible(false);
  }, [scene]);

  const handleLoad = () => setSceneVisible(true);

  return (
    <Container>
      <SceneWrapper className={sceneVisible ? "visible" : ""}>
        <Spline scene={scene} onLoad={handleLoad} />
      </SceneWrapper>
    </Container>
  );
}

/*
function SplineWithLoader_OLD_GIF_VERSION({ scene, useGif }: { scene: string; useGif: boolean }) {
  // const [visible, setVisible] = useState(useGif);
  // const [fadingOut, setFadingOut] = useState(false);
  // const [sceneVisible, setSceneVisible] = useState(!useGif);
  // const [sceneLoaded, setSceneLoaded] = useState(false);
  // const [delayPassed, setDelayPassed] = useState(!useGif);
  // const [shouldMount, setShouldMount] = useState(false);
  // const [gifIndex, setGifIndex] = useState(0);
  // const [isCyclingComplete, setIsCyclingComplete] = useState(false);
  // const loadingGifs = [loadingGif1, loadingGif2, loadingGif3, loadingGif4, loadingGif5];

  // useEffect(() => {
  //   setVisible(useGif);
  //   setFadingOut(false);
  //   setSceneVisible(!useGif);
  //   setSceneLoaded(false);
  //   setDelayPassed(!useGif);
  //   setShouldMount(false);
  //   setGifIndex(0);
  //   setIsCyclingComplete(false);

  //   const mountTimer = setTimeout(() => setShouldMount(true), 300);
  //   let delayTimer: number | undefined;
  //   let cycleInterval: number | undefined;

  //   if (useGif) {
  //     delayTimer = window.setTimeout(() => setDelayPassed(true), 7000);
  //     cycleInterval = window.setInterval(() => {
  //       setGifIndex(prev => {
  //         if (prev < loadingGifs.length - 1) return prev + 1;
  //         setIsCyclingComplete(true);
  //         if (cycleInterval) window.clearInterval(cycleInterval);
  //         return prev;
  //       });
  //     }, 1000);
  //   }
  //   return () => {
  //     clearTimeout(mountTimer);
  //     if (delayTimer) clearTimeout(delayTimer);
  //     if (cycleInterval) clearInterval(cycleInterval);
  //   };
  // }, [scene, useGif]);

  // useEffect(() => {
  //   if (useGif && sceneLoaded && delayPassed) {
  //     setFadingOut(true);
  //     const a = setTimeout(() => setSceneVisible(true), 250);
  //     const b = setTimeout(() => setVisible(false), 500);
  //     return () => { clearTimeout(a); clearTimeout(b); };
  //   }
  // }, [useGif, sceneLoaded, delayPassed]);

  // const handleLoad = () => setSceneLoaded(true);
  // const currentGif = isCyclingComplete ? loadingDoneGif : loadingGifs[gifIndex];

  // return (... old JSX with GIFs ...)
}
*/

function AppShell() {
  return (
    <>
      <GlobalStyle />

      <RouteTransitionOverlay showOnFirstMount />

      <Navbar>
        <Title>
          <TitleLink to="/">Gabriel Holder - Software Developer</TitleLink>
        </Title>
        <NavItems>
          <NavItem>
            <StyledLink to="/Home">Home</StyledLink>
          </NavItem>
          <NavItem>
            <StyledLink to="/About-Me">About Me</StyledLink>
          </NavItem>

          <DropdownWrapper>
            Internships
            <DropdownMenu>
              <DropdownItem>
                <StyledLink to="/Leidos">Leidos</StyledLink>
              </DropdownItem>
              <DropdownItem>
                <StyledLink to="/Boeing">Boeing</StyledLink>
              </DropdownItem>
              <DropdownItem>
                <StyledLink to="/NTTDATA">NTTDATA</StyledLink>
              </DropdownItem>
            </DropdownMenu>
          </DropdownWrapper>

          <DropdownWrapper>
            Projects
            <DropdownMenu>
              <DropdownItem>
                <Link href="https://stockquantum.netlify.app/">
                  Quantum Stock Trader
                </Link>
              </DropdownItem>
              {/* <DropdownItem>
                <Link
                  href="https://nlp.discovery.cs.vt.edu/"
                >
                  Virginia Tech Grade Distribution NLP
                </Link>
              </DropdownItem> */}
              <DropdownItem>
                <Link
                  href="https://machine-learning-astrology-classifer.netlify.app/"
                >
                  Machine Learning Horoscope Classifier
                </Link>
              </DropdownItem>

            </DropdownMenu>
          </DropdownWrapper>
          <DropdownWrapper>
            Academia
            <DropdownMenu>
              <DropdownItem>
                <StyledLink to="/Undergraduate-Teaching-Assistant">
                  Undergraduate Teaching Assistant
                </StyledLink>
              </DropdownItem>
              <DropdownItem>
                <Link
                  href="https://codekids.cs.vt.edu/about_us"
                  target="_blank"
                  rel="noreferrer"
                >
                  University Study
                </Link>
              </DropdownItem>
            </DropdownMenu>
          </DropdownWrapper>
        </NavItems>
      </Navbar>

      <Routes>
        <Route
          path="/"
          element={
            <SplineWithLoader scene="https://prod.spline.design/MqvAGzeHATJJPp1Y/scene.splinecode" />
          }
        />
        <Route
          path="/Home"
          element={
            <SplineWithLoader scene="https://prod.spline.design/MqvAGzeHATJJPp1Y/scene.splinecode" />
          }
        />
        <Route
          path="/About-Me"
          element={
            <SplineWithLoader scene="https://prod.spline.design/WU2irVw9VyJ1X97g/scene.splinecode" />
          }
        />
        <Route
          path="/Boeing"
          element={
            <SplineWithLoader scene="https://prod.spline.design/p0HJlTnE-FgqihVl/scene.splinecode" />
          }
        />
        <Route
          path="/NTTDATA"
          element={
            <SplineWithLoader scene="https://prod.spline.design/3x-2Z1CLob7WXNr2/scene.splinecode" />
          }
        />
        <Route
          path="/Undergraduate-Teaching-Assistant"
          element={
            <SplineWithLoader scene="https://prod.spline.design/xcaEoWHmaZZXqe7Z/scene.splinecode" />
          }
        />
        <Route
          path="/Leidos"
          element={
            <SplineWithLoader scene="https://prod.spline.design/Wo7y8jPzloruXN6n/scene.splinecode" />
          }
        />
      </Routes>

      <Logo>gholder</Logo>

      <div style={{ display: "none" }}>
        Gabriel Holder is a university student software developer with
        experience in TypeScript, React, and JavaScript. Holder was part of the
        team that developed "CodeKids" a project that tied for the People's
        Choice award at the 2024 Virginia Tech Undergraduate Research in
        Computer Science competition. He's interned with multiple government
        contractors as a student at Virginia Tech, he combines full-stack
        development with leadership experience.
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}
